import React, { FC } from "react";
import { Button, DatePicker, Input, Select } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { IBaseTariffsUrlParams } from "~/pages/Directory/BaseTariffs/model/filterBaseTariffs";
import {
  parseDate,
  toDateString,
  toSelectFilter,
} from "~/pages/Directory/shared/filterHelpers";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";

import s from "~/pages/Directory/shared/DirectoryFilter.less";

interface ISelectOption {
  value: string;
  label: string;
}

interface BaseTariffsFilterProps {
  positionOptions: ISelectOption[];
  positionOptionsLoading?: boolean;
}

const BaseTariffsFilter: FC<BaseTariffsFilterProps> = ({
  positionOptions,
  positionOptionsLoading,
}) => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const {
    position,
    positionName,
    price,
    createdFrom,
    createdTo,
    updatedFrom,
    updatedTo,
    author,
  } = useURLParamsGetter<IBaseTariffsUrlParams>();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      position: toSelectFilter(position, positionName),
      price: price || "",
      createdFrom: parseDate(createdFrom),
      createdTo: parseDate(createdTo),
      updatedFrom: parseDate(updatedFrom),
      updatedTo: parseDate(updatedTo),
      author: author || "",
    },
  });

  const onSubmit = (data) => {
    setParams({
      position: data.position?.value || undefined,
      positionName: data.position?.label || undefined,
      price: data.price || undefined,
      createdFrom: toDateString(data.createdFrom) || undefined,
      createdTo: toDateString(data.createdTo) || undefined,
      updatedFrom: toDateString(data.updatedFrom) || undefined,
      updatedTo: toDateString(data.updatedTo) || undefined,
      author: data.author || undefined,
      page: "0",
    });
  };

  const cancelForm = () => {
    reset({
      position: null,
      price: "",
      createdFrom: null,
      createdTo: null,
      updatedFrom: null,
      updatedTo: null,
      author: "",
    });
    setParams({
      position: undefined,
      positionName: undefined,
      price: undefined,
      createdFrom: undefined,
      createdTo: undefined,
      updatedFrom: undefined,
      updatedTo: undefined,
      author: undefined,
      page: "0",
    });
  };

  const renderField = (labelKey: string, content: React.ReactNode) => (
    <div className={s.fieldWrapper}>
      <span className={s.label}>{t(labelKey)}</span>
      {content}
    </div>
  );

  const renderTextInput = (name: string) => (
    <Controller
      name={name}
      control={control}
      render={({ onChange, value, onBlur }) => (
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          placeholder={t("directory.filter.inputPlaceholder")}
        />
      )}
    />
  );

  const renderDatePicker = (name: string, placeholder: string) => (
    <Controller
      control={control}
      name={name}
      render={({ onChange, value, onBlur }) => (
        <DatePicker
          placeholder={placeholder}
          format="DD.MM.YYYY"
          value={value}
          onBlur={onBlur}
          onChange={(date) => onChange(date ?? null)}
        />
      )}
    />
  );

  const renderDateRange = (nameFrom: string, nameTo: string) => (
    <div className={s.rangeWrapp}>
      {renderDatePicker(nameFrom, t("directory.filter.dateFrom"))}
      <span className={s.rangeDivider}>-</span>
      {renderDatePicker(nameTo, t("directory.filter.dateTo"))}
    </div>
  );

  return (
    <form className={s.formFilter} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.filterWrapper}>
        {renderField(
          "directory.baseTariffs.filter.position",
          <Controller
            name="position"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                allowClear
                showSearch
                value={
                  value
                    ? positionOptions.find(
                        (option) => option.value === value.value,
                      ) || value
                    : value
                }
                onChange={(option) => onChange(option ?? null)}
                options={positionOptions}
                loading={positionOptionsLoading}
                placeholder={t("directory.filter.inputPlaceholder")}
                filterOption={filterOptionByLabel}
              />
            )}
          />,
        )}
        {renderField(
          "directory.baseTariffs.filter.price",
          renderTextInput("price"),
        )}
        {renderField(
          "directory.baseTariffs.filter.createdAt",
          renderDateRange("createdFrom", "createdTo"),
        )}
        {renderField(
          "directory.baseTariffs.filter.updatedAt",
          renderDateRange("updatedFrom", "updatedTo"),
        )}
        {renderField(
          "directory.baseTariffs.filter.author",
          renderTextInput("author"),
        )}
      </div>
      <div className={s.buttons}>
        <Button
          htmlType="button"
          color="secondary"
          variant="text"
          bold={false}
          fontSize="12"
          size="medium"
          onClick={cancelForm}
        >
          {t("directory.reset")}
        </Button>
        <Button
          htmlType="submit"
          bold
          color="secondary"
          fontSize="12"
          size="medium"
        >
          {t("directory.show")}
        </Button>
      </div>
    </form>
  );
};

export default BaseTariffsFilter;
